/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { ApiError } from "api_clients/api_error";
import { isErrorResponse } from "api_clients/api_response";
import {
  getEmployeeId,
  getOrganizationId,
  isBackdoorEnabled,
} from "api_clients/backdoor";
import { IAuthContext } from "providers/CognitoAuthProvider";

type HttpMethod = "GET" | "POST" | "PATCH" | "DELETE";

const getUrl = (endpoint: string) => {
  const origin = process.env.REACT_APP_API_ORIGIN;
  if (endpoint.substr(0, 1) !== "/") {
    // eslint-disable-next-line no-param-reassign
    endpoint = `/${endpoint}`;
  }

  return `${origin}${endpoint}`;
};

const callApi = async (
  endpoint: string,
  method: HttpMethod,
  headers: Headers,
  body?: any
) => {
  const data = { body: body ? JSON.stringify(body) : null };
  const result = await fetch(getUrl(endpoint), {
    method,
    headers,
    ...data,
  });

  if (isErrorResponse(result)) {
    throw new ApiError(result.status, result, await result.text());
  }

  return result;
};

const callApiWithAuth = async (
  auth: IAuthContext,
  endpoint: string,
  method: HttpMethod,
  body?: any
) => {
  if (!auth.isAuthenticated) return false;
  if (!auth.session) return false;

  const headers = new Headers({
    Authorization: `Bearer ${(await auth.getSession())
      .getIdToken()
      .getJwtToken()}`,
    "Content-Type": "application/json",
  });
  const result = await callApi(endpoint, method, headers, body);
  const data = await result.json();
  return data;
};

const callApiByBackdoor = async (
  endpoint: string,
  method: HttpMethod,
  body?: any
) => {
  const headers = new Headers({
    "X-Backdoor-Organization-Id": `${getOrganizationId()}`,
    "X-Backdoor-Employee-Id": `${getEmployeeId()}`,
    "Content-Type": "application/json",
  });
  const result = await callApi(endpoint, method, headers, body);
  const data = await result.json();
  return data;
};

const getByBackdoor = async (endpoint: string) => {
  if (!isBackdoorEnabled()) return false;
  return callApiByBackdoor(endpoint, "GET");
};

const get = async (auth: IAuthContext, endpoint: string) => {
  if (isBackdoorEnabled()) return getByBackdoor(endpoint);
  return callApiWithAuth(auth, endpoint, "GET");
};

const writeByBackdoor = async (
  endpoint: string,
  method: HttpMethod,
  body?: any
) => {
  if (!isBackdoorEnabled()) return false;
  return callApiByBackdoor(endpoint, method, body);
};

const write = async (
  auth: IAuthContext,
  method: HttpMethod,
  endpoint: string,
  body?: any
) => {
  if (isBackdoorEnabled()) return writeByBackdoor(endpoint, method, body);
  return callApiWithAuth(auth, endpoint, method, body);
};

const post = async (auth: IAuthContext, endpoint: string, body: any) =>
  write(auth, "POST", endpoint, body);

const postByBackdoor = async (endpoint: string, body: any) =>
  writeByBackdoor(endpoint, "POST", body);

const update = async (auth: IAuthContext, endpoint: string, body: any) =>
  write(auth, "PATCH", endpoint, body);

const updateByBackdoor = async (endpoint: string, body: any) =>
  writeByBackdoor(endpoint, "PATCH", body);

const del = async (auth: IAuthContext, endpoint: string) =>
  write(auth, "DELETE", endpoint);

const delByBackdoor = async (endpoint: string) =>
  writeByBackdoor(endpoint, "DELETE");

// backdoor系が外に出ているのは危ない気がするので消して良いかもしれない
// テストとかで使いたい系であれば `_` で始めるとかにしないと事故る可能性
export const CustomerAPIClient = {
  get,
  getByBackdoor,
  post,
  postByBackdoor,
  update,
  updateByBackdoor,
  del,
  delByBackdoor,
};
/* eslint-enable */
